/**
 * Input Component
 * @author Muhammad Arsan
 * @description A reusable input component using Material-UI's Input component
 */
/**
 * Input component for Form fields
 * @param {InputProps} props - The component props
 * @param {number} [props.label=SPINNER_DEFAULTS.SIZE] - The label of the field
 * @param {string} [props.iconVisibleSrc=SPINNER_DEFAULTS.COLOR] - The visible icon of the field
 * @param {number} [props.iconHiddenSrc=SPINNER_DEFAULTS.THICKNESS] - The hidden icon of the field
 * @returns {JSX.Element} The rendered Input component
 */

import React, { useRef, useEffect } from "react";
import { TextField, InputAdornment } from "@mui/material";
import { FORM_PROPERTIES } from "../contants";
import type { SxProps, Theme } from "@mui/material";
import type { InputFieldProps } from "../types/inputfield";
import { TextFieldStyles } from "../styles/TextField.styles";
import { mergeSelectSx, resolveInputFieldControlHeight, textOutlinedInputSx } from "@config/theme/formControls";
import { SelectFieldWrapper } from "../../Select/shared/SelectFieldWrapper";

/**
 * A reusable input component for forms, built on MUI's TextField.
 * @author Muhammad Arsan
 */
const InputField: React.FC<InputFieldProps> = ({
	id,
	label,
	type = "text",
	placeholder,
	value,
	onChange,
	disabled = false,
	error = false,
	helperText = "",
	startIcon,
	endIcon,
	multiline = false,
	rows,
	className,
	labelClassName,
	onClick,
	isClickable,
	"aria-label": ariaLabel,
	"aria-describedby": ariaDescribedby,
	required,
	size = "small",
	min,
	max,
	...rest
}) => {
	const inputRef = useRef<HTMLInputElement>(null);

	// Extract InputProps from rest to merge them properly
	const {
		InputProps: customInputProps,
		inputProps: htmlInputPropsFromRest,
		slotProps: slotPropsFromRest,
		select: isSelect,
		hideSelectChevron = false,
		selectControlHeight,
		sx: sxProp,
		...restProps
	} = rest;

	const htmlInputFromRest = (htmlInputPropsFromRest as Record<string, unknown> | undefined) ?? {};
	const resolvedHtmlInputProps: Record<string, unknown> = { ...htmlInputFromRest };
	const resolvedMin = min ?? htmlInputFromRest.min;
	const resolvedMax = max ?? htmlInputFromRest.max;
	if (resolvedMin !== undefined && resolvedMin !== null && String(resolvedMin) !== "") {
		resolvedHtmlInputProps.min = resolvedMin;
	} else {
		delete resolvedHtmlInputProps.min;
	}
	if (resolvedMax !== undefined && resolvedMax !== null && String(resolvedMax) !== "") {
		resolvedHtmlInputProps.max = resolvedMax;
	} else {
		delete resolvedHtmlInputProps.max;
	}

	const resolvedSlotProps = {
		...(slotPropsFromRest as Record<string, unknown> | undefined),
		htmlInput: {
			...((slotPropsFromRest as { htmlInput?: Record<string, unknown> } | undefined)?.htmlInput ?? {}),
			...resolvedHtmlInputProps,
		},
	};

	// Update date input colors / min-max constraints on the native input
	useEffect(() => {
		if (type === "date" && inputRef.current) {
			const input = inputRef.current;
			if (value && value.toString().trim() !== "") {
				// Has value - dark color
				input.style.color = "#000";
				input.style.webkitTextFillColor = "#000";
			} else {
				// No value - light gray for placeholder
				input.style.color = "#6B7280";
				input.style.webkitTextFillColor = "#6B7280";
			}

			const maxAttr = resolvedHtmlInputProps.max;
			const minAttr = resolvedHtmlInputProps.min;
			if (maxAttr !== undefined && maxAttr !== null && String(maxAttr) !== "") {
				input.setAttribute("max", String(maxAttr));
			} else {
				input.removeAttribute("max");
			}
			if (minAttr !== undefined && minAttr !== null && String(minAttr) !== "") {
				input.setAttribute("min", String(minAttr));
			} else {
				input.removeAttribute("min");
			}
		}
	}, [type, value, resolvedHtmlInputProps.max, resolvedHtmlInputProps.min]);

	const controlHeight = resolveInputFieldControlHeight(size);
	const { sx: customInputSx, ...restCustomInputProps } = customInputProps ?? {};

	const selectInputProps = {
		startAdornment: startIcon ? (
			<InputAdornment position="start" sx={{ marginRight: "-3px" }}>
				{startIcon}
			</InputAdornment>
		) : undefined,
		endAdornment: endIcon ? <InputAdornment position="end">{endIcon}</InputAdornment> : undefined,
		...restCustomInputProps,
		...(customInputSx ? { sx: customInputSx } : {}),
	};

	const textInputProps = {
		...selectInputProps,
		sx: [
			multiline
				? {
						...textOutlinedInputSx(error, controlHeight),
						height: "auto",
						minHeight: controlHeight,
						alignItems: "flex-start",
						paddingTop: "10px",
						paddingBottom: "10px",
					}
				: textOutlinedInputSx(error, controlHeight),
			customInputSx,
		].filter(Boolean) as SxProps<Theme>,
	};

	const textFieldSx = [
		TextFieldStyles(isClickable, size, error),
		multiline
			? {
					"& .MuiOutlinedInput-root": {
						height: "auto !important",
						minHeight: `${controlHeight}px`,
						alignItems: "flex-start",
					},
					"& .MuiOutlinedInput-root textarea": {
						height: "auto !important",
						minHeight: "0 !important",
						lineHeight: 1.5,
						overflow: "auto !important",
						paddingTop: "0 !important",
						paddingBottom: "0 !important",
						boxSizing: "border-box",
						resize: "vertical",
					},
				}
			: null,
		sxProp,
	].filter(Boolean) as SxProps<Theme>;

	return (
		<div>
			{label && (
				<label className={`mb-2 block font-medium text-sm sm:text-base ${labelClassName ?? ""}`} htmlFor={id}>
					{label}
					{required && <span className="text-red-500">{FORM_PROPERTIES.REQUIRED_ASTERIK}</span>}
				</label>
			)}
			{isSelect ? (
				<>
					<SelectFieldWrapper
						controlHeight={selectControlHeight}
						disabled={disabled}
						className={hideSelectChevron ? "[&>button:last-of-type]:hidden" : undefined}>
						<TextField
							id={id}
							className={`${className ?? ""} ${isClickable ? "cursor-pointer" : ""}`}
							fullWidth
							variant="outlined"
							type={type}
							placeholder={placeholder}
							value={value ?? ""}
							onChange={onChange}
							onClick={onClick}
							disabled={disabled}
							error={error}
							helperText={undefined}
							rows={rows}
							multiline={multiline}
							aria-label={ariaLabel || (typeof label === "string" ? label : undefined)}
							aria-describedby={ariaDescribedby}
							aria-invalid={error ? "true" : "false"}
							sx={mergeSelectSx(error, sxProp as SxProps<Theme> | undefined)}
							SelectProps={{
								displayEmpty: true,
								renderValue: (selected: unknown): React.ReactNode => {
									if (!selected || selected === "") {
										return <span style={{ color: "#6B7280" }}>{placeholder}</span>;
									}

									const selectedOption = React.Children.toArray(restProps.children).find(
										child =>
											React.isValidElement(child) &&
											(child.props as Record<string, unknown>)?.value?.toString() === selected?.toString(),
									) as React.ReactElement | undefined;

									const children = (selectedOption?.props as Record<string, unknown>)?.children;
									if (children && typeof children === "string") {
										return <span style={{ color: "#111827" }}>{children}</span>;
									}
									return <span style={{ color: "#111827" }}>{String(selected)}</span>;
								},
							}}
							{...restProps}
							InputProps={selectInputProps}
							inputRef={inputRef}
							inputProps={resolvedHtmlInputProps}
							slotProps={resolvedSlotProps}
							select
						/>
					</SelectFieldWrapper>
					{helperText ? (
						<p
							className={`mt-1 font-['Poppins',sans-serif] text-[11px] leading-none ${error ? "text-[#d32f2f]" : "text-gray-500"}`}>
							{helperText}
						</p>
					) : null}
				</>
			) : (
				<TextField
					id={id}
					className={`${className ?? ""} ${isClickable ? "cursor-pointer" : ""}`}
					fullWidth
					variant="outlined"
					type={type}
					placeholder={placeholder}
					value={value ?? ""}
					onChange={onChange}
					onClick={onClick}
					disabled={disabled}
					error={error}
					helperText={helperText}
					rows={rows}
					multiline={multiline}
					aria-label={ariaLabel || (typeof label === "string" ? label : undefined)}
					aria-describedby={ariaDescribedby}
					aria-invalid={error ? "true" : "false"}
					sx={textFieldSx}
					SelectProps={{
						displayEmpty: true,
						renderValue: (selected: unknown): React.ReactNode => {
							if (!selected || selected === "") {
								return <span style={{ color: "#6B7280" }}>{placeholder}</span>;
							}

							const selectedOption = React.Children.toArray(rest.children).find(
								child =>
									React.isValidElement(child) &&
									(child.props as Record<string, unknown>)?.value?.toString() === selected?.toString(),
							) as React.ReactElement | undefined;

							const children = (selectedOption?.props as Record<string, unknown>)?.children;
							if (children && typeof children === "string") {
								return <span style={{ color: "#000000" }}>{children}</span>;
							}
							return <span style={{ color: "#000000" }}>{String(selected)}</span>;
						},
					}}
					{...restProps}
					InputProps={textInputProps}
					inputRef={inputRef}
					inputProps={resolvedHtmlInputProps}
					slotProps={resolvedSlotProps}
				/>
			)}
		</div>
	);
};

export default InputField;
